import React from 'react';
import clsx from 'clsx';
import { makeStyles, Theme } from '@material-ui/core/styles';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import SortArrowIcon from '../icons/SortArrowIcon';
import NamedBox from '../atoms/NamedBox';
import { COLOR_BACKGROUND_WARM, COLOR_BACKGROUND_LIGHT, COLOR_BORDER, COLOR_MIDDLE_GRAY, COLOR_TEXT, COLOR_WHITE, COLOR_BLUE } from '../styles/colors';
import { FONT_PROXIMA_NOVA } from '../styles/fonts';
import { useTableSort } from '../hooks/useTableSort';
import { JUCONAIAProductionRow } from '../types/CollegeProductionData';


interface JucoProductionTableProps {
  className?: string;
  data?: JUCONAIAProductionRow[] | null;
  label?: string;
}

const useStyles = makeStyles((theme: Theme) => {
  const firstColWidth = 80;
  const secondColWidth = 270;
  const thirdColWidth = 60;
  const topHeaderHeight = 64;

  return {
    root: {
      border: 0,
    },
    hideNamedBoxHeader: {
      display: 'none',
    },
    headerName: {
      fontFamily: FONT_PROXIMA_NOVA,
    },
    tableContainer: {
      maxHeight: 360,
      overflow: 'auto',
    },
    table: {
      width: '100%',
      minWidth: 960,
      borderCollapse: 'collapse' as const,
      border: `1px solid ${COLOR_BACKGROUND_WARM}`,
      fontFamily: FONT_PROXIMA_NOVA,
      fontSize: theme.typography.pxToRem(16),
    },
    tableTitleCell: {
      backgroundColor: COLOR_BACKGROUND_WARM,
      height: topHeaderHeight,
      padding: theme.spacing(1, 2),
      textAlign: 'left' as const,
      whiteSpace: 'nowrap',
      ...theme.typography.h2,
      fontWeight: 700,
      boxSizing: 'border-box' as const,
    },
    stickyHeaderTop: {
      position: 'sticky' as const,
      top: 0,
      zIndex: 3,
    },
    theadGroupCell: {
      verticalAlign: 'bottom',
      backgroundColor: COLOR_BACKGROUND_WARM,
      borderLeft: `1px solid ${COLOR_BACKGROUND_WARM}`,
      color: COLOR_TEXT,
      fontWeight: 700,
      textAlign: 'center' as const,
      height: 64,
      padding: 0,
      boxSizing: 'border-box' as const,
    },
    tableHeaderBlock: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 36,
      padding: theme.spacing(1),
      lineHeight: 1.2,
      backgroundColor: COLOR_MIDDLE_GRAY,
    },
    theadCell: {
      overflow: 'hidden',
      border: `1px solid ${COLOR_BACKGROUND_WARM}`,
      borderTop: `2px solid ${COLOR_MIDDLE_GRAY}`,
      padding: theme.spacing(1),
      whiteSpace: 'nowrap' as const,
      color: COLOR_TEXT,
      fontWeight: 700,
      textAlign: 'center' as const,
      userSelect: 'none' as const,
      cursor: 'pointer',
    },
    stickyHeaderSecond: {
      position: 'sticky' as const,
      zIndex: 2,
      top: topHeaderHeight,
      backgroundColor: COLOR_WHITE,
    },
    sortLabel: {
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: theme.spacing(0.25),
    },
    sortIcon: {
      opacity: 1,
      color: COLOR_MIDDLE_GRAY,

      '& svg': {
        display: 'block',
        width: 9,
        height: 'auto',
      },
    },
    thFirstCol: {
      zIndex: 3,
      left: 0,
      width: firstColWidth,
    },
    thSecondCol: {
      width: secondColWidth,
      whiteSpace: 'normal',
      textAlign: 'left',
    },
    thThirdCol: {
      width: thirdColWidth,
      whiteSpace: 'normal',
      textAlign: 'left',
    },
    stickyCol: {
      position: 'sticky' as const,
    },
    bodyRow: {
    },
    bodyCell: {
      padding: theme.spacing(1.5, 1),
      border: `1px solid ${COLOR_BACKGROUND_WARM}`,
      textAlign: 'center' as const,
      whiteSpace: 'nowrap' as const,
      backgroundColor: COLOR_WHITE,
      fontWeight: 600,
      
      'tr:nth-child(odd) &': {
        backgroundColor: COLOR_BACKGROUND_LIGHT,
      },
    },
    bodyCellFirst: {
      position: 'sticky' as const,
      left: 0,
    },
    bodyCellSecond: {
      textAlign: 'left' as const,
      whiteSpace: 'normal',
      fontWeight: 700,
    },
  };
}, { name: 'JucoProductionTable' });

export default function JucoProductionTable(props: JucoProductionTableProps) {
  const { className, data, label = 'JUCO' } = props;
  const classes = useStyles();
  const rows: JUCONAIAProductionRow[] = data || [];
  const { sortKey, sortDirection, handleSort, sortedRows } = useTableSort<JUCONAIAProductionRow, keyof JUCONAIAProductionRow>({
    rows,
    initialSortKey: 'year',
    initialDirection: 'desc',
    nextKeyDirection: 'desc',
  });

  function renderSortableHeader(
    label: string,
    key: keyof JUCONAIAProductionRow,
    className: string,
    rowSpan?: number,
  ) {
    const isActive = sortKey === key;
    return (
      <th className={className} rowSpan={rowSpan}>
        <TableSortLabel
          active={isActive}
          direction={isActive ? sortDirection : 'asc'}
          className={classes.sortLabel}
          hideSortIcon={false}
          IconComponent={(iconProps) => (
            <SortArrowIcon
              {...iconProps}
              className={`${iconProps.className} ${classes.sortIcon}`}
              style={{
                color: isActive ? COLOR_BLUE : '#b5b5b5',
              }}
            />
          )}
          onClick={() => handleSort(key)}
        >
          {label}
        </TableSortLabel>
      </th>
    );
  }

  return (
    <div className={className}>
      <NamedBox
        className={clsx(classes.root)}
        headerClassName={classes.hideNamedBoxHeader}
        name=""
      >
        <div className={classes.tableContainer}>
          <table className={classes.table}>
            <thead>
              <tr>
                <th
                  className={clsx(classes.stickyHeaderTop, classes.tableTitleCell)}
                  colSpan={3}
                >
                  {label} Offensive Stats
                </th>
                <th
                  className={clsx(classes.theadGroupCell, classes.stickyHeaderTop)}
                  colSpan={5}
                >
                  <div className={classes.tableHeaderBlock}>Passing</div>
                </th>
                <th
                  className={clsx(classes.theadGroupCell, classes.stickyHeaderTop)}
                  colSpan={3}
                >
                  <div className={classes.tableHeaderBlock}>Rushing</div>
                </th>
                <th
                  className={clsx(classes.theadGroupCell, classes.stickyHeaderTop)}
                  colSpan={3}
                >
                  <div className={classes.tableHeaderBlock}>Receiving</div>
                </th>
              </tr>
              <tr>
                {renderSortableHeader(
                  'Year',
                  'year',
                  clsx(
                    classes.theadCell,
                    classes.stickyHeaderSecond,
                    classes.stickyCol,
                    classes.thFirstCol,
                  ),
                  2,
                )}
                {renderSortableHeader(
                  'Team',
                  'teamName',
                  clsx(
                    classes.theadCell,
                    classes.stickyHeaderSecond,
                    classes.thSecondCol,
                  ),
                  2,
                )}
                {renderSortableHeader(
                  'G',
                  'gamesPlayed',
                  clsx(
                    classes.theadCell,
                    classes.stickyHeaderSecond,
                    classes.thThirdCol,
                  ),
                  2,
                )}
                {/* Passing */}
                {renderSortableHeader('Comp', 'passingComp', clsx(classes.theadCell, classes.stickyHeaderSecond))}
                {renderSortableHeader('Att', 'passingAtt', clsx(classes.theadCell, classes.stickyHeaderSecond))}
                {renderSortableHeader('Yds', 'passingYds', clsx(classes.theadCell, classes.stickyHeaderSecond))}
                {renderSortableHeader('TD', 'passingTD', clsx(classes.theadCell, classes.stickyHeaderSecond))}
                {renderSortableHeader('INT', 'passingInt', clsx(classes.theadCell, classes.stickyHeaderSecond))}
                {/* Rushing */}
                {renderSortableHeader('Att', 'rushingAtt', clsx(classes.theadCell, classes.stickyHeaderSecond))}
                {renderSortableHeader('Yds', 'rushingYds', clsx(classes.theadCell, classes.stickyHeaderSecond))}
                {renderSortableHeader('TD', 'rushingTD', clsx(classes.theadCell, classes.stickyHeaderSecond))}
                {/* Receiving */}
                {renderSortableHeader('Rec', 'receiving', clsx(classes.theadCell, classes.stickyHeaderSecond))}
                {renderSortableHeader('Yds', 'receivingYds', clsx(classes.theadCell, classes.stickyHeaderSecond))}
                {renderSortableHeader('TD', 'receivingTD', clsx(classes.theadCell, classes.stickyHeaderSecond))}
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((row) => (
                <tr key={`${row.year}-${row.teamName}`} className={classes.bodyRow}>
                  <td className={clsx(classes.bodyCell, classes.bodyCellFirst)}>
                    {row.year}
                  </td>
                  <td className={clsx(classes.bodyCell, classes.bodyCellSecond)}>
                    {row.teamName}
                  </td>
                  <td className={classes.bodyCell}>{row.gamesPlayed}</td>
                  {/* Passing */}
                  <td className={classes.bodyCell}>{row.passingComp}</td>
                  <td className={classes.bodyCell}>{row.passingAtt}</td>
                  <td className={classes.bodyCell}>{row.passingYds}</td>
                  <td className={classes.bodyCell}>{row.passingTD}</td>
                  <td className={classes.bodyCell}>{row.passingInt}</td>
                  {/* Rushing */}
                  <td className={classes.bodyCell}>{row.rushingAtt}</td>
                  <td className={classes.bodyCell}>{row.rushingYds}</td>
                  <td className={classes.bodyCell}>{row.rushingTD}</td>
                  {/* Receiving */}
                  <td className={classes.bodyCell}>{row.receiving}</td>
                  <td className={classes.bodyCell}>{row.receivingYds}</td>
                  <td className={classes.bodyCell}>{row.receivingTD}</td>
                  {/* Defense */}
                  {/* <td className={classes.bodyCell}>{row.defense.tckls}</td>
                  <td className={classes.bodyCell}>{row.defense.sacks}</td>
                  <td className={classes.bodyCell}>{row.defense.ints}</td> */}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </NamedBox>

      <NamedBox
        className={clsx(classes.root)}
        headerClassName={classes.hideNamedBoxHeader}
        name=""
      >
        <div className={classes.tableContainer}>
          <table className={classes.table}>
            <thead>
              <tr>
                <th
                  className={clsx(classes.stickyHeaderTop, classes.tableTitleCell)}
                  colSpan={3}
                >
                  {label} Defensive Stats
                </th>
                <th
                  className={clsx(classes.theadGroupCell, classes.stickyHeaderTop)}
                  colSpan={10}
                >
                  <div className={classes.tableHeaderBlock}>Defense</div>
                </th>
              </tr>
              <tr>
                {renderSortableHeader(
                  'Year',
                  'year',
                  clsx(
                    classes.theadCell,
                    classes.stickyHeaderSecond,
                    classes.stickyCol,
                    classes.thFirstCol,
                  ),
                  2,
                )}
                {renderSortableHeader(
                  'Team',
                  'teamName',
                  clsx(
                    classes.theadCell,
                    classes.stickyHeaderSecond,
                    classes.thSecondCol,
                  ),
                  2,
                )}
                {renderSortableHeader(
                  'G',
                  'gamesPlayed',
                  clsx(
                    classes.theadCell,
                    classes.stickyHeaderSecond,
                    classes.thThirdCol,
                  ),
                  2,
                )}
                {/* Defense */}
                {renderSortableHeader('Solo Tckls', 'defenseSoloTackles', clsx(classes.theadCell, classes.stickyHeaderSecond))}
                {renderSortableHeader('Asst Tckls', 'defenseAssistedTackles', clsx(classes.theadCell, classes.stickyHeaderSecond))}
                {renderSortableHeader('Total Tckls', 'defenseTotalTackles', clsx(classes.theadCell, classes.stickyHeaderSecond))}
                {renderSortableHeader('Sacks', 'defenseSack', clsx(classes.theadCell, classes.stickyHeaderSecond))}
                {renderSortableHeader('Sack Yards', 'defenseSackYds', clsx(classes.theadCell, classes.stickyHeaderSecond))}
                {renderSortableHeader('TFLs', 'defenseTFL', clsx(classes.theadCell, classes.stickyHeaderSecond))}
                {renderSortableHeader('TFL Yards', 'defenseTFLYds', clsx(classes.theadCell, classes.stickyHeaderSecond))}
                {renderSortableHeader('PBUs', 'defensePassBreak', clsx(classes.theadCell, classes.stickyHeaderSecond))}
                {renderSortableHeader('INTs', 'defenseInt', clsx(classes.theadCell, classes.stickyHeaderSecond))}
                {renderSortableHeader('FFs', 'defenseFF', clsx(classes.theadCell, classes.stickyHeaderSecond))}
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((row) => (
                <tr key={`${row.year}-${row.teamName}-def`} className={classes.bodyRow}>
                  <td className={clsx(classes.bodyCell, classes.bodyCellFirst)}>
                    {row.year}
                  </td>
                  <td className={clsx(classes.bodyCell, classes.bodyCellSecond)}>
                    {row.teamName}
                  </td>
                  <td className={classes.bodyCell}>{row.gamesPlayed}</td>
                  {/* Defense */}
                  <td className={classes.bodyCell}>{row.defenseSoloTackles}</td>
                  <td className={classes.bodyCell}>{row.defenseAssistedTackles}</td>
                  <td className={classes.bodyCell}>{row.defenseTotalTackles}</td>
                  <td className={classes.bodyCell}>{row.defenseSack}</td>
                  <td className={classes.bodyCell}>{row.defenseSackYds}</td>
                  <td className={classes.bodyCell}>{row.defenseTFL}</td>
                  <td className={classes.bodyCell}>{row.defenseTFLYds}</td>
                  <td className={classes.bodyCell}>{row.defensePassBreak}</td>
                  <td className={classes.bodyCell}>{row.defenseInt}</td>
                  <td className={classes.bodyCell}>{row.defenseFF}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </NamedBox>

      <NamedBox
        className={clsx(classes.root)}
        headerClassName={classes.hideNamedBoxHeader}
        name=""
      >
        <div className={classes.tableContainer}>
          <table className={classes.table}>
            <thead>
              <tr>
                <th
                  className={clsx(classes.stickyHeaderTop, classes.tableTitleCell)}
                  colSpan={3}
                >
                  {label} Special Teams Stats
                </th>
                <th
                  className={clsx(classes.theadGroupCell, classes.stickyHeaderTop)}
                  colSpan={4}
                >
                  <div className={classes.tableHeaderBlock}>Kicking</div>
                </th>
                <th
                  className={clsx(classes.theadGroupCell, classes.stickyHeaderTop)}
                  colSpan={3}
                >
                  <div className={classes.tableHeaderBlock}>Punting</div>
                </th>
                <th
                  className={clsx(classes.theadGroupCell, classes.stickyHeaderTop)}
                  colSpan={4}
                >
                  <div className={classes.tableHeaderBlock}>Kick Returns</div>
                </th>
                <th
                  className={clsx(classes.theadGroupCell, classes.stickyHeaderTop)}
                  colSpan={4}
                >
                  <div className={classes.tableHeaderBlock}>Punt Returns</div>
                </th>
              </tr>
              <tr>
                {renderSortableHeader(
                  'Year',
                  'year',
                  clsx(
                    classes.theadCell,
                    classes.stickyHeaderSecond,
                    classes.stickyCol,
                    classes.thFirstCol,
                  ),
                  2,
                )}
                {renderSortableHeader(
                  'Team',
                  'teamName',
                  clsx(
                    classes.theadCell,
                    classes.stickyHeaderSecond,
                    classes.thSecondCol,
                  ),
                  3,
                )}
                {renderSortableHeader(
                  'G',
                  'gamesPlayed',
                  clsx(classes.theadCell, classes.stickyHeaderSecond),
                  2,
                )}
                {/* Kicking */}
                {renderSortableHeader('FGM', 'kickingFGM', clsx(classes.theadCell, classes.stickyHeaderSecond))}
                {renderSortableHeader('FGA', 'kickingFGA', clsx(classes.theadCell, classes.stickyHeaderSecond))}
                {renderSortableHeader('XP', 'kickingXP', clsx(classes.theadCell, classes.stickyHeaderSecond))}
                {renderSortableHeader('XPA', 'kickingXPA', clsx(classes.theadCell, classes.stickyHeaderSecond))}
                {/* Punting */}
                {renderSortableHeader('Punts', 'punts', clsx(classes.theadCell, classes.stickyHeaderSecond))}
                {renderSortableHeader('Yds', 'puntYds', clsx(classes.theadCell, classes.stickyHeaderSecond))}
                {renderSortableHeader('Avg', 'puntsAverage', clsx(classes.theadCell, classes.stickyHeaderSecond))}
                {/* Kick Returns */}
                {renderSortableHeader('KR', 'kickReturns', clsx(classes.theadCell, classes.stickyHeaderSecond))}
                {renderSortableHeader('Yds', 'kickReturnYds', clsx(classes.theadCell, classes.stickyHeaderSecond))}
                {renderSortableHeader('Avg', 'kickReturnAverage', clsx(classes.theadCell, classes.stickyHeaderSecond))}
                {renderSortableHeader('TD', 'kickReturnTD', clsx(classes.theadCell, classes.stickyHeaderSecond))}
                {/* Punt Returns */}
                {renderSortableHeader('PR', 'puntRet', clsx(classes.theadCell, classes.stickyHeaderSecond))}
                {renderSortableHeader('Yds', 'puntRetYds', clsx(classes.theadCell, classes.stickyHeaderSecond))}
                {renderSortableHeader('Avg', 'puntRetAverage', clsx(classes.theadCell, classes.stickyHeaderSecond))}
                {renderSortableHeader('TD', 'puntRetTD', clsx(classes.theadCell, classes.stickyHeaderSecond))}
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((row) => (
                <tr key={`${row.year}-${row.teamName}-st`} className={classes.bodyRow}>
                  <td className={clsx(classes.bodyCell, classes.bodyCellFirst)}>
                    {row.year}
                  </td>
                  <td className={clsx(classes.bodyCell, classes.bodyCellSecond)}>
                    {row.teamName}
                  </td>
                  <td className={classes.bodyCell}>{row.gamesPlayed}</td>
                  {/* Kicking */}
                  <td className={classes.bodyCell}>{row.kickingFGM}</td>
                  <td className={classes.bodyCell}>{row.kickingFGA}</td>
                  <td className={classes.bodyCell}>{row.kickingXP}</td>
                  <td className={classes.bodyCell}>{row.kickingXPA}</td>
                  {/* Punting */}
                  <td className={classes.bodyCell}>{row.punts}</td>
                  <td className={classes.bodyCell}>{row.puntYds}</td>
                  <td className={classes.bodyCell}>{row.puntsAverage}</td>
                  {/* Kick Returns */}
                  <td className={classes.bodyCell}>{row.kickReturns}</td>
                  <td className={classes.bodyCell}>{row.kickReturnYds}</td>
                  <td className={classes.bodyCell}>{row.kickReturnAverage}</td>
                  <td className={classes.bodyCell}>{row.kickReturnTD}</td>
                  {/* Punt Returns */}
                  <td className={classes.bodyCell}>{row.puntRet}</td>
                  <td className={classes.bodyCell}>{row.puntRetYds}</td>
                  <td className={classes.bodyCell}>{row.puntRetAverage}</td>
                  <td className={classes.bodyCell}>{row.puntRetTD}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </NamedBox>
    </div>
  );
}

